import { isMatch } from 'lodash';
import type { PathsWithMethod } from 'openapi-typescript-helpers';
import {
  createMutateHook,
  createQueryHook,
  type TypesForGetRequest,
} from 'swr-openapi';
import { identityApi, paymentApi, provisionApi, txmApi } from './clients';
import type {
  CoreSchema,
  IdentitySchema,
  PaymentSchema,
  ProvisionSchema,
} from './generated';

/**
 * Hook for fetching from identity-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useIdentity = createQueryHook(identityApi, 'identity-api');

/**
 * Hook for mutating identity-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useIdentityMutate = createMutateHook(
  identityApi,
  'identity-api',
  isMatch,
);

/**
 * Type of identity-service get request object based on path
 * @example
 * type UserRequest = IdentityRequest<'/users/{userId}'>
 */
export type IdentityRequest<
  Path extends PathsWithMethod<IdentitySchema.paths, 'get'>,
> = TypesForGetRequest<IdentitySchema.paths, Path>;

/**
 * Hook for fetching from txm-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useTxm = createQueryHook(txmApi, 'txm-api');

/**
 * Hook for mutating txm-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const useTxmMutate = createMutateHook(txmApi, 'txm-api', isMatch);

/**
 * Type of txm-service get request object based on path
 * @example
 * type UserRequest = TxmRequest<'/users/{userId}'>
 */
export type TxmRequest<Path extends PathsWithMethod<CoreSchema.paths, 'get'>> =
  TypesForGetRequest<CoreSchema.paths, Path>;

/**
 * Hook for fetching from payment-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const usePayment = createQueryHook(paymentApi, 'payment-api');

/**
 * Hook for mutating payment-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 */
export const usePaymentMutate = createMutateHook(
  paymentApi,
  'payment-api',
  isMatch,
);

/**
 * Type of payment-service get request object based on path
 * @example
 * type PaymentRequest = PaymentRequest<'/users/{userId}'>
 */
export type PaymentRequest<
  Path extends PathsWithMethod<PaymentSchema.paths, 'get'>,
> = TypesForGetRequest<PaymentSchema.paths, Path>;

/**
 * Hook for fetching from provision-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @knipignore provision-service client layer, kept alongside the other
 * services so calling it is a one-line change rather than a re-scaffold.
 */
export const useProvision = createQueryHook(provisionApi, 'provision-api');

/**
 * Hook for mutating provision-service. Type safe version of useSwr.
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @knipignore See useProvision.
 */
export const useProvisionMutate = createMutateHook(
  provisionApi,
  'provision-api',
  isMatch,
);

/**
 * Type of provision-service get request object based on path
 * @example
 * type ProvisionRequest = ProvisionRequest<'/users/{userId}'>
 * @knipignore See useProvision.
 */
export type ProvisionRequest<
  Path extends PathsWithMethod<ProvisionSchema.paths, 'get'>,
> = TypesForGetRequest<ProvisionSchema.paths, Path>;
